import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ActionButton from "@/components/base/ActionButton";
import { brand, journeySteps } from "@/mocks/navigation";
import { partners } from "@/mocks/partners";

const approachPoints = [
  { icon: "ri-search-eye-line", title: "Assessment First", text: "Every retrofit begins with the vehicle — condition, architecture, weight and usage — before any configuration is proposed." },
  { icon: "ri-equalizer-line", title: "No Universal Specifications", text: "We do not publish a standard battery, range or cost, because the right configuration is vehicle-specific." },
  { icon: "ri-shield-check-line", title: "Safety by Design", text: "Electrical safety, battery safety and thermal considerations are engineering requirements, not afterthoughts." },
  { icon: "ri-customer-service-2-line", title: "Serviceable Outcomes", text: "Serviceability is considered during design so the converted vehicle can be maintained over its extended life." },
];

const sustainabilityPoints = [
  "Extending the working life of vehicles already on the road",
  "Reducing dependence on fossil fuels for daily mobility",
  "Supporting phased electrification instead of wholesale replacement",
  "Encouraging a more circular approach to vehicle ownership",
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us | KAL LORDS ZERO21 DJT EV Retrofit Technologies"
        description="Learn about the KAL LORDS ZERO21 DJT technology collaboration — vision, mission, engineering approach and EV retrofit ecosystem."
        keywords="EV retrofit company, EV conversion solutions, vehicle electrification, electric mobility India"
        path="/about"
      />
      <PageHero
        eyebrow="About Us"
        breadcrumb="About"
        title="Building Practical Electric Mobility Through Retrofit"
        description="KAL LORDS ZERO21 DJT is an EV retrofit technology collaboration. We believe a suitable vehicle deserves a second life as an electric vehicle rather than being replaced."
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ActionButton to="/partners" variant="primary" size="md" icon="ri-team-line">
            Meet Our Technology Partners
          </ActionButton>
          <ActionButton to="/ev-retrofit" variant="outlineLight" size="md" iconRight="ri-arrow-right-line">
            Understand EV Retrofit
          </ActionButton>
        </div>
      </PageHero>

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Reveal>
              <article className="h-full rounded-lg border border-background-200 bg-background-100 p-7 md:p-9">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-foreground-950 text-primary-400">
                  <i className="ri-eye-line text-2xl" />
                </span>
                <h2 className="mt-5 text-xl font-bold text-foreground-950 md:text-2xl">Our Vision</h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                  To accelerate the transition to electric mobility by making vehicle transformation
                  technically practical, scalable and accessible.
                </p>
              </article>
            </Reveal>
            <Reveal delay={0.1}>
              <article className="h-full rounded-lg border border-background-200 bg-background-100 p-7 md:p-9">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-foreground-950">
                  <i className="ri-focus-3-line text-2xl" />
                </span>
                <h2 className="mt-5 text-xl font-bold text-foreground-950 md:text-2xl">Our Mission</h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                  To combine EV engineering, retrofit expertise, vehicle technology and charging
                  infrastructure to create next-generation electric mobility solutions.
                </p>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Our Approach"
              title="Engineering-Led, Claim-Safe and Vehicle-Specific"
              description="We treat retrofit as an engineering activity. That means asking the right technical questions before promising anything."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {approachPoints.map((point, index) => (
              <Reveal key={point.title} delay={index * 0.06}>
                <div className="flex h-full items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                    <i className={`${point.icon} text-xl`} />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950">{point.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{point.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Technology Collaboration"
              title="Three Technology Strengths Working Together"
              description="The collaboration brings together retrofit experience, EV engineering and charging infrastructure. Each partner contributes a distinct strength."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
            {partners.map((partner, index) => (
              <Reveal key={partner.key} delay={index * 0.08}>
                <div className="h-full rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md border border-background-200 bg-foreground-950 font-heading text-sm font-extrabold text-background-50">
                    {partner.monogram}
                  </span>
                  <h3 className="mt-4 text-lg font-bold text-foreground-950">{partner.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{partner.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10">
              <ActionButton to="/partners" variant="dark" size="md" iconRight="ri-arrow-right-line">
                View Partner Profiles
              </ActionButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <SectionHeading
                eyebrow="Sustainable Mobility"
                title="More Life From Every Vehicle"
                description="Sustainable mobility is not only about new vehicles. It is also about what we do with the vehicles already in use. Retrofit keeps suitable vehicles in service and gives them an electric future."
              />
              <ul className="mt-8 space-y-3">
                {sustainabilityPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-leaf-line mt-0.5 text-primary-600" />
                    {point}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="overflow-hidden rounded-lg border border-background-200">
                <div className="h-64 w-full md:h-[420px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=clean%20minimal%20electric%20vehicle%20charging%20at%20an%20architectural%20depot%20at%20dusk%2C%20graphite%20tones%20with%20subtle%20green%20lighting%2C%20premium%20industrial%20photography%2C%20soft%20gradients&width=1200&height=900&seq=klz-about-01&orientation=landscape"
                    alt="Electric vehicle charging as part of a sustainable mobility ecosystem"
                    title="Sustainable electric mobility through EV retrofit"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="w-full bg-foreground-950 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              align="center"
              tone="dark"
              eyebrow="Our Ecosystem"
              title="From Existing Vehicle to Sustainable Mobility"
              description="The ecosystem connects the vehicle, the retrofit, energy storage, charging, connected services and long-term service support."
            />
          </Reveal>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
            {journeySteps.map((step, index) => (
              <Reveal key={step} delay={index * 0.04}>
                <div className="flex items-center gap-3">
                  <span className="rounded-full border border-background-50/15 bg-background-50/[0.05] px-4 py-2 font-label text-xs font-medium text-background-100">
                    {step}
                  </span>
                  {index < journeySteps.length - 1 ? (
                    <i className="ri-arrow-right-s-line text-primary-400/70" />
                  ) : null}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-12 text-center font-label text-sm font-semibold uppercase tracking-[0.16em] text-primary-300">
              {brand.closing}
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}